import type { Accessor, Component, JSX } from "solid-js";
import { createSignal, onCleanup, onMount, Show } from "solid-js";
import type { ElementDropTargetEventBasePayload } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { attachClosestEdge, extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { disableNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview";
import { preventUnhandled } from "@atlaskit/pragmatic-drag-and-drop/prevent-unhandled";
import type { Edge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/types";
import { Portal } from "solid-js/web";
import type { DraggableData } from "./util";
import { isDraggableDataOfType } from "./util";

export type DraggableProps = {
  data: DraggableData;
  /** Prevents Draggable container from intercepting mouse events on child elements (e.g. selecting text in inputs) */
  useDragHandleAsDragElement?: boolean;
  dragHandle: HTMLDivElement | (() => HTMLDivElement);
  isLast: boolean;
  preview: Component;
  children: (state: Accessor<DragItemState>) => JSX.Element;
};

type DragItemState = {
  type: "idle" | "is-dragging" | "is-dragging-over";
  closestEdge?: Edge | null;
};

const idle = { type: "idle" as const };

const Draggable: Component<DraggableProps> = (props) => {
  let element!: HTMLDivElement;

  const [dragState, setDragState] = createSignal<DragItemState>(idle);

  const [previewPosition, setPreviewPosition] = createSignal<{
    x: number;
    y: number;
  } | null>(null);

  onMount(() => {
    const onChange = ({ source, self }: ElementDropTargetEventBasePayload) => {
      if (
        !isDraggableDataOfType(source.data, props.data.type) ||
        !isDraggableDataOfType(self.data, props.data.type) ||
        source.data.id === self.data.id
      ) {
        setDragState(idle);
        return;
      }

      const closestEdge = extractClosestEdge(self.data);
      setDragState((current) => {
        if (current.type === "is-dragging-over" && current.closestEdge === closestEdge) {
          return current;
        }
        return { type: "is-dragging-over", closestEdge };
      });
    };

    const resolvedHandle = typeof props.dragHandle === "function" ? props.dragHandle() : props.dragHandle;

    onCleanup(
      combine(
        draggable({
          element: props.useDragHandleAsDragElement === true ? resolvedHandle : element,
          dragHandle: resolvedHandle,
          getInitialData: () => props.data,
          onGenerateDragPreview: ({ nativeSetDragImage }) => {
            // We need to use a custom drag preview. The native browser preview comes with a default opacity that can't be adjusted
            // https://atlassian.design/components/pragmatic-drag-and-drop/web-platform-design-constraints#native-drag-previews
            disableNativeDragPreview({ nativeSetDragImage });
          },
          onDragStart: ({ location }) => {
            preventUnhandled.start();
            const { clientX, clientY } = location.initial.input;
            setPreviewPosition({ x: clientX, y: clientY });
            setDragState({ type: "is-dragging" });
          },
          onDrag: ({ location }) => {
            const { clientX, clientY } = location.current.input;
            setPreviewPosition({ x: clientX, y: clientY });
          },
          onDrop: () => {
            preventUnhandled.stop();
            setPreviewPosition(null);
            setDragState(idle);
          },
        }),
        dropTargetForElements({
          element,
          canDrop: ({ source }) =>
            isDraggableDataOfType(source.data, props.data.type) && source.data.id !== props.data.id,
          getData: ({ input }) =>
            attachClosestEdge(props.data, {
              element,
              input,
              allowedEdges: props.isLast ? ["top", "bottom"] : ["top"],
            }),
          onDragEnter: onChange,
          onDrag: onChange,
          onDragLeave: () => {
            setDragState(idle);
          },
          onDrop: () => {
            setDragState(idle);
          },
        }),
      ),
    );
  });

  return (
    <>
      <div ref={element} class="relative w-full" classList={{ "opacity-50": dragState().type === "is-dragging" }}>
        {props.children(dragState)}
      </div>

      <Show when={previewPosition()}>
        {(position) => (
          <Portal>
            <div
              class="pointer-events-none fixed cursor-grabbing"
              style={{
                top: `${position().y}px`,
                left: `${position().x}px`,
                transform: "translate(-15px, -15px)", // slight offset from cursor
                "z-index": 9999,
              }}
            >
              <props.preview />
            </div>
          </Portal>
        )}
      </Show>
    </>
  );
};

export default Draggable;
